import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt = (n, d=2) => n != null && n !== '' ? Number(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d}) : '—'

const XIcon      = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const PlusIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const TrashIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
const UploadIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
const DownIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
const SendIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${type==='error'?'bg-red-600':'bg-[#1A7A4A]'}`}>
      {message}
    </div>
  )
}

const ConfirmOverlay = ({ message, onConfirm, onCancel, confirmLabel='Delete', confirmColor='bg-red-600' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
      <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
        <button onClick={onConfirm} className={`px-4 py-2 text-sm text-white rounded-lg ${confirmColor}`}>{confirmLabel}</button>
      </div>
    </div>
  </div>
)

const EMPTY_ROW = {
  resource_type: 'labor', ownership: 'direct', subcontractor_name: '',
  entry_date: new Date().toISOString().split('T')[0],
  zone: '', area: '', activity_type: '', description: '',
  workers_count: '', qty_executed: '', sub_code: '', notes: '',
}

export default function SiteAllocationDraft({ initProject }) {
  const [projects, setProjects]             = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [drafts, setDrafts]                 = useState([])
  const [subCodes, setSubCodes]             = useState([]) // from budget, for coding dropdown
  const [loading, setLoading]               = useState(false)
  const [toast, setToast]                   = useState(null)
  const [confirmDelete, setConfirmDelete]   = useState(null)
  const [confirmSend, setConfirmSend]       = useState(null) // single row or 'bulk'

  // Filters
  const [resourceFilter, setResourceFilter]     = useState('all') // all | labor | equipment
  const [ownershipFilter, setOwnershipFilter]   = useState('all') // all | direct | subcontractor
  const [statusFilter, setStatusFilter]         = useState('all') // all | draft | coded | sent
  const [dateFrom, setDateFrom]                 = useState('')
  const [dateTo, setDateTo]                     = useState('')

  // Entry modal
  const [showModal, setShowModal]           = useState(false)
  const [editEntry, setEditEntry]           = useState(null)
  const [form, setForm]                     = useState({ ...EMPTY_ROW })

  // Bulk selection
  const [selected, setSelected]             = useState(new Set())

  // Upload
  const [uploadPreview, setUploadPreview]   = useState(null)
  const [uploading, setUploading]           = useState(false)
  const fileRef                             = useRef(null)

  const showToast = (msg, type='success') => setToast({ message: msg, type })
  const h = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data||[]))
  }, [])

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadDrafts(selectedProject.id)
      loadSubCodes(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadDrafts = useCallback(async (pid) => {
    setLoading(true)
    const { data } = await supabase
      .from('site_allocation_drafts')
      .select('*')
      .eq('project_id', pid)
      .order('entry_date', { ascending: false })
    setDrafts(data || [])
    setLoading(false)
  }, [])

  // Same budget sub_code source pattern as Labour Productivity — covers labor, equipment, subcontract, material
  const loadSubCodes = async (pid) => {
    const { data } = await supabase.from('tender_direct_items')
      .select('budget_material_code, labour_code, budget_sc_code, budget_equipment_code, row_type, description, resource_description')
      .eq('project_id', pid)
      .in('row_type', ['material', 'labour', 'subcontract', 'equipment'])
    const map = {}
    ;(data||[]).forEach(r => {
      let sc = null
      if (r.row_type === 'labour')           sc = r.labour_code
      else if (r.row_type === 'material')    sc = r.budget_material_code
      else if (r.row_type === 'subcontract') sc = r.budget_sc_code
      else if (r.row_type === 'equipment')   sc = r.budget_equipment_code
      if (!sc || map[sc]) return
      map[sc] = { sub_code: sc, description: r.resource_description || r.description || '' }
    })
    setSubCodes(Object.values(map).sort((a,b)=>a.sub_code.localeCompare(b.sub_code)))
  }

  // ── Save entry (add / edit) ────────────────────────────────
  const handleSave = async () => {
    if (!form.entry_date)    return showToast('التاريخ مطلوب', 'error')
    if (form.ownership === 'subcontractor' && !form.subcontractor_name.trim())
      return showToast('اسم المقاول مطلوب', 'error')
    if (form.resource_type === 'labor' && !form.workers_count)
      return showToast('عدد العمال مطلوب', 'error')
    if (form.resource_type === 'equipment' && !form.description.trim())
      return showToast('وصف المعدة مطلوب', 'error')
    if (!form.qty_executed) return showToast('الكمية المنفذة مطلوبة', 'error')

    const payload = {
      project_id:         selectedProject.id,
      resource_type:       form.resource_type,
      ownership:           form.ownership,
      subcontractor_name:  form.ownership === 'subcontractor' ? form.subcontractor_name.trim() : null,
      entry_date:          form.entry_date,
      zone:                form.zone.trim() || null,
      area:                form.area.trim() || null,
      activity_type:       form.activity_type.trim() || null,
      description:         form.description.trim() || null,
      workers_count:       form.resource_type === 'labor' ? (parseInt(form.workers_count) || 0) : null,
      qty_executed:        parseFloat(form.qty_executed) || 0,
      sub_code:            form.sub_code.trim() || null,
      status:              form.sub_code.trim() ? 'coded' : 'draft',
      notes:               form.notes.trim() || null,
    }

    const { error } = editEntry
      ? await supabase.from('site_allocation_drafts').update(payload).eq('id', editEntry.id)
      : await supabase.from('site_allocation_drafts').insert(payload)

    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(editEntry ? 'تم التحديث' : 'تم الحفظ')
    setShowModal(false)
    loadDrafts(selectedProject.id)
  }

  // ── Inline sub_code coding directly from the table ─────────
  const handleCodeInline = async (row, subCode) => {
    const { error } = await supabase.from('site_allocation_drafts')
      .update({ sub_code: subCode || null, status: subCode ? 'coded' : 'draft' })
      .eq('id', row.id)
    if (error) return showToast('فشل: ' + error.message, 'error')
    setDrafts(prev => prev.map(d => d.id === row.id ? { ...d, sub_code: subCode || null, status: subCode ? 'coded' : 'draft' } : d))
  }

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    await supabase.from('site_allocation_drafts').delete().eq('id', id)
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadDrafts(selectedProject.id)
  }

  // ── Send: copy coded rows into Labour/Equipment Productivity ──
  const sendRows = async (rows) => {
    const directLabor      = rows.filter(r => r.resource_type === 'labor'     && r.ownership === 'direct' && r.status === 'coded')
    const directEquipment   = rows.filter(r => r.resource_type === 'equipment' && r.ownership === 'direct' && r.status === 'coded')
    const subcontractorRows = rows.filter(r => r.ownership === 'subcontractor' && r.status === 'coded')
    const uncoded            = rows.filter(r => r.status !== 'coded')

    if (uncoded.length > 0) {
      showToast(`${uncoded.length} صف لسه محتاج Sub Code قبل الإرسال`, 'error')
      return
    }

    let sentCount = 0

    if (directLabor.length > 0) {
      const payload = directLabor.map(r => ({
        project_id:    r.project_id,
        sub_code:      r.sub_code,
        activity_type: r.activity_type,
        entry_date:    r.entry_date,
        workers:       r.workers_count || 0,
        qty_executed:  r.qty_executed,
        notes:         r.notes,
      }))
      const { error } = await supabase.from('labour_productivity_entries').insert(payload)
      if (error) { showToast('فشل إرسال العمالة الذاتية: ' + error.message, 'error'); return }
      sentCount += directLabor.length
    }

    if (directEquipment.length > 0) {
      const payload = directEquipment.map(r => ({
        project_id:    r.project_id,
        sub_code:      r.sub_code,
        activity_type: r.activity_type,
        entry_date:    r.entry_date,
        equipment_desc: r.description,
        qty_executed:  r.qty_executed,
        notes:         r.notes,
      }))
      const { error } = await supabase.from('equipment_productivity_entries').insert(payload)
      if (error) { showToast('فشل إرسال المعدات الذاتية: ' + error.message, 'error'); return }
      sentCount += directEquipment.length
    }

    // Subcontractor rows: marked as sent for now — linking to Subcontracts page is a future step
    const allSentIds = [...directLabor, ...directEquipment, ...subcontractorRows].map(r => r.id)
    if (allSentIds.length > 0) {
      await supabase.from('site_allocation_drafts').update({ status: 'sent' }).in('id', allSentIds)
    }

    showToast(`تم إرسال ${sentCount + subcontractorRows.length} صف${subcontractorRows.length > 0 ? ` (${subcontractorRows.length} مقاول — بانتظار الربط بصفحة Subcontracts)` : ''}`)
    setSelected(new Set())
    setConfirmSend(null)
    loadDrafts(selectedProject.id)
  }

  // ── Download Template ─────────────────────────────────────
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['entry_date', 'resource_type', 'ownership', 'subcontractor_name', 'zone', 'area', 'activity_type', 'description', 'workers_count', 'qty_executed', 'notes'],
      ['2026-01-05', 'labor', 'direct', '', 'Zone A', 'Level 1', 'Formwork', 'Carpenter Crew 1', 8, 12.5, ''],
      ['2026-01-05', 'equipment', 'direct', '', 'Zone A', 'Level 1', 'Concrete Pour', 'Tower Crane TC-1', '', 1, ''],
      ['2026-01-05', 'labor', 'subcontractor', 'ABC Steel Co.', 'Zone B', 'Level 2', 'Steel Fixing', 'Steel Fixers', 15, 8.0, ''],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Site Allocation')
    XLSX.writeFile(wb, 'site_allocation_draft_template.xlsx')
  }

  // ── Parse & Upload Excel ──────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' })
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
      const preview = rows.map(r => {
        const resource_type = String(r.resource_type || r['Resource Type'] || 'labor').trim().toLowerCase()
        const ownership      = String(r.ownership || r['Ownership'] || 'direct').trim().toLowerCase()
        return {
          entry_date:          String(r.entry_date || r['Date'] || '').trim(),
          resource_type:       resource_type === 'equipment' ? 'equipment' : 'labor',
          ownership:           ownership === 'subcontractor' ? 'subcontractor' : 'direct',
          subcontractor_name:  String(r.subcontractor_name || r['Subcontractor Name'] || '').trim() || null,
          zone:                String(r.zone || r['Zone'] || '').trim() || null,
          area:                String(r.area || r['Area'] || '').trim() || null,
          activity_type:       String(r.activity_type || r['Activity Type'] || '').trim() || null,
          description:         String(r.description || r['Description'] || '').trim() || null,
          workers_count:       parseInt(r.workers_count || r['Workers Count'] || 0) || null,
          qty_executed:        parseFloat(r.qty_executed || r['Qty Executed'] || 0) || 0,
          sub_code:            null,
          status:              'draft',
          notes:               String(r.notes || r['Notes'] || '').trim() || null,
        }
      }).filter(r => r.entry_date && r.qty_executed > 0)
      setUploadPreview(preview)
    }
    reader.readAsArrayBuffer(file)
  }

  const handleConfirmUpload = async () => {
    if (!uploadPreview?.length) return
    setUploading(true)
    const payload = uploadPreview.map(r => ({ ...r, project_id: selectedProject.id }))
    const BATCH = 100
    let ok = 0
    for (let i = 0; i < payload.length; i += BATCH) {
      const { error } = await supabase.from('site_allocation_drafts').insert(payload.slice(i, i + BATCH))
      if (!error) ok += Math.min(BATCH, payload.length - i)
    }
    setUploading(false)
    showToast(`تم رفع ${ok} سجل كـ Draft — محتاجين تكويد قبل الإرسال`)
    setUploadPreview(null)
    loadDrafts(selectedProject.id)
  }

  // ── Filter ────────────────────────────────────────────────
  const filtered = drafts.filter(d => {
    if (resourceFilter !== 'all' && d.resource_type !== resourceFilter) return false
    if (ownershipFilter !== 'all' && d.ownership !== ownershipFilter) return false
    if (statusFilter !== 'all' && d.status !== statusFilter) return false
    if (dateFrom && d.entry_date < dateFrom) return false
    if (dateTo && d.entry_date > dateTo) return false
    return true
  })

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleSelectAll = () => {
    const codedVisible = filtered.filter(d => d.status === 'coded')
    if (selected.size === codedVisible.length && codedVisible.length > 0) setSelected(new Set())
    else setSelected(new Set(codedVisible.map(d => d.id)))
  }

  const statusBadge = (status) => {
    const map = {
      draft: { bg:'#F3F4F6', text:'#6B7280', label:'Draft' },
      coded: { bg:'#FEF3C7', text:'#B45309', label:'Coded' },
      sent:  { bg:'#DCFCE7', text:'#15803D', label:'Sent' },
    }
    const s = map[status] || map.draft
    return <span style={{background:s.bg,color:s.text,padding:'2px 9px',borderRadius:10,fontSize:10.5,fontWeight:700}}>{s.label}</span>
  }

  const counts = {
    draft: drafts.filter(d=>d.status==='draft').length,
    coded: drafts.filter(d=>d.status==='coded').length,
    sent:  drafts.filter(d=>d.status==='sent').length,
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-[#1C1A18]">Site Allocation — Draft</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">Site enters raw labor/equipment allocation here. Cost Control codes it with a Sub Code, then sends it to Labour/Equipment Productivity.</p>
          </div>
          <select
            value={selectedProject?.id || ''}
            onChange={e => setSelectedProject(projects.find(p => p.id === e.target.value) || null)}
            className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30">
            <option value="">— Select Project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {selectedProject && (
          <>
            {/* Summary chips */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-[#6B7280]">{counts.draft}</div>
                <div className="text-xs text-[#6B7280] mt-1">Draft — بانتظار التكويد</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-amber-600">{counts.coded}</div>
                <div className="text-xs text-[#6B7280] mt-1">Coded — جاهز للإرسال</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-emerald-600">{counts.sent}</div>
                <div className="text-xs text-[#6B7280] mt-1">Sent — تم الإرسال</div>
              </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 flex flex-wrap items-center gap-3">
              <select value={resourceFilter} onChange={e=>setResourceFilter(e.target.value)}
                className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs">
                <option value="all">All Resources</option>
                <option value="labor">Labor</option>
                <option value="equipment">Equipment</option>
              </select>
              <select value={ownershipFilter} onChange={e=>setOwnershipFilter(e.target.value)}
                className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs">
                <option value="all">All Ownership</option>
                <option value="direct">Direct</option>
                <option value="subcontractor">Subcontractor</option>
              </select>
              <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
                className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs">
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="coded">Coded</option>
                <option value="sent">Sent</option>
              </select>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
                className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs"/>
              <span className="text-xs text-[#9CA3AF]">to</span>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
                className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs"/>

              <div className="flex-1"/>

              {selected.size > 0 && (
                <button onClick={() => setConfirmSend('bulk')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A7A4A] text-white rounded-lg text-xs font-medium">
                  <SendIcon/> Send Selected ({selected.size})
                </button>
              )}
              <button onClick={downloadTemplate}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E7EB] rounded-lg text-xs font-medium text-[#6B7280]">
                <DownIcon/> Template
              </button>
              <label className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E7EB] rounded-lg text-xs font-medium text-[#6B7280] cursor-pointer">
                <UploadIcon/> Upload Excel
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
                  onChange={e => handleFileSelect(e.target.files[0])}/>
              </label>
              <button onClick={() => { setForm({...EMPTY_ROW}); setEditEntry(null); setShowModal(true) }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-medium">
                <PlusIcon/> Add Entry
              </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="px-3 py-3">
                        <input type="checkbox"
                          checked={selected.size > 0 && selected.size === filtered.filter(d=>d.status==='coded').length}
                          onChange={toggleSelectAll}/>
                      </th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Date</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Resource</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Ownership</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Zone / Area</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Activity</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Description</th>
                      <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">Workers</th>
                      <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">Qty</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Sub Code</th>
                      <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Status</th>
                      <th className="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {loading ? (
                      <tr><td colSpan={12} className="text-center py-8 text-[#9CA3AF]">Loading...</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={12} className="text-center py-8 text-[#9CA3AF]">لا يوجد بيانات</td></tr>
                    ) : filtered.map(d => (
                      <tr key={d.id} className="hover:bg-[#FAFAFA]">
                        <td className="px-3 py-2">
                          {d.status === 'coded' && (
                            <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)}/>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{d.entry_date}</td>
                        <td className="px-3 py-2 capitalize">{d.resource_type}</td>
                        <td className="px-3 py-2">
                          {d.ownership === 'direct' ? (
                            <span className="text-[#1A7A4A] font-medium">Direct</span>
                          ) : (
                            <span className="text-amber-700 font-medium">Sub: {d.subcontractor_name}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[#6B7280] whitespace-nowrap">{[d.zone, d.area].filter(Boolean).join(' / ') || '—'}</td>
                        <td className="px-3 py-2 text-[#6B7280]">{d.activity_type || '—'}</td>
                        <td className="px-3 py-2 max-w-[180px] truncate" title={d.description}>{d.description || '—'}</td>
                        <td className="px-3 py-2 text-right">{d.workers_count ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{fmt(d.qty_executed)}</td>
                        <td className="px-3 py-2">
                          {d.status === 'sent' ? (
                            <span className="font-mono text-[#1B3A6B]">{d.sub_code}</span>
                          ) : (
                            <select
                              value={d.sub_code || ''}
                              onChange={e => handleCodeInline(d, e.target.value)}
                              className="border border-[#E5E7EB] rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1B3A6B]/40 max-w-[130px]">
                              <option value="">— Code —</option>
                              {subCodes.map(s => <option key={s.sub_code} value={s.sub_code}>{s.sub_code}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="px-3 py-2">{statusBadge(d.status)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {d.status === 'coded' && (
                              <button onClick={() => setConfirmSend(d)} title="Send"
                                className="text-[#1A7A4A] hover:text-[#15693F]"><SendIcon/></button>
                            )}
                            {d.status !== 'sent' && (
                              <button onClick={() => setConfirmDelete(d.id)} title="Delete"
                                className="text-red-400 hover:text-red-600"><TrashIcon/></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirm Delete */}
      {confirmDelete && (
        <ConfirmOverlay
          message="متأكد من حذف السجل ده؟"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete)}/>
      )}

      {/* Confirm Send */}
      {confirmSend && (
        <ConfirmOverlay
          message={confirmSend === 'bulk'
            ? `هيتم إرسال ${selected.size} صف — الذاتي هيروح لـ Labor/Equipment Productivity، والمقاول هيتحفظ بانتظار الربط بصفحة Subcontracts.`
            : `هيتم إرسال السجل ده — ${confirmSend.ownership === 'direct' ? 'هيروح لـ ' + (confirmSend.resource_type === 'labor' ? 'Labor' : 'Equipment') + ' Productivity' : 'هيتحفظ بانتظار الربط بصفحة Subcontracts'}.`}
          confirmLabel="Send"
          confirmColor="bg-[#1A7A4A]"
          onCancel={() => setConfirmSend(null)}
          onConfirm={() => sendRows(confirmSend === 'bulk' ? drafts.filter(d => selected.has(d.id)) : [confirmSend])}/>
      )}

      {/* Upload Preview Modal */}
      {uploadPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-base font-bold text-[#1C1A18]">Preview — {uploadPreview.length} rows</h2>
              <button onClick={() => setUploadPreview(null)}><XIcon/></button>
            </div>
            <div className="p-5 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    {['Date','Resource','Ownership','Sub-Name','Zone','Area','Activity','Description','Workers','Qty'].map(hh=>(
                      <th key={hh} className="px-2 py-2 text-left text-[#6B7280] font-semibold uppercase whitespace-nowrap">{hh}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                  {uploadPreview.map((r,i)=>(
                    <tr key={i}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.entry_date}</td>
                      <td className="px-2 py-1.5 capitalize">{r.resource_type}</td>
                      <td className="px-2 py-1.5 capitalize">{r.ownership}</td>
                      <td className="px-2 py-1.5">{r.subcontractor_name || '—'}</td>
                      <td className="px-2 py-1.5">{r.zone || '—'}</td>
                      <td className="px-2 py-1.5">{r.area || '—'}</td>
                      <td className="px-2 py-1.5">{r.activity_type || '—'}</td>
                      <td className="px-2 py-1.5 max-w-[160px] truncate">{r.description || '—'}</td>
                      <td className="px-2 py-1.5 text-right">{r.workers_count ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(r.qty_executed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setUploadPreview(null)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm">Cancel</button>
              <button onClick={handleConfirmUpload} disabled={uploading}
                className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {uploading ? 'Uploading...' : `Upload ${uploadPreview.length} rows`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-base font-bold text-[#1C1A18]">{editEntry ? 'Edit Entry' : 'Add Entry'}</h2>
              <button onClick={() => setShowModal(false)}><XIcon/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Resource Type *</label>
                  <select value={form.resource_type} onChange={e=>h('resource_type', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                    <option value="labor">Labor</option>
                    <option value="equipment">Equipment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Ownership *</label>
                  <select value={form.ownership} onChange={e=>h('ownership', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                    <option value="direct">Direct (Company)</option>
                    <option value="subcontractor">Subcontractor</option>
                  </select>
                </div>
              </div>

              {form.ownership === 'subcontractor' && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Subcontractor Name *</label>
                  <input value={form.subcontractor_name} onChange={e=>h('subcontractor_name', e.target.value)}
                    placeholder="e.g. ABC Steel Co."
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Date *</label>
                  <input type="date" value={form.entry_date} onChange={e=>h('entry_date', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Activity Type</label>
                  <input value={form.activity_type} onChange={e=>h('activity_type', e.target.value)}
                    placeholder="e.g. Formwork"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Zone</label>
                  <input value={form.zone} onChange={e=>h('zone', e.target.value)}
                    placeholder="e.g. Zone A"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Area</label>
                  <input value={form.area} onChange={e=>h('area', e.target.value)}
                    placeholder="e.g. Level 1"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">
                  {form.resource_type === 'labor' ? 'Crew / Team Description' : 'Equipment Description *'}
                </label>
                <input value={form.description} onChange={e=>h('description', e.target.value)}
                  placeholder={form.resource_type === 'labor' ? 'e.g. Carpenter Crew 1' : 'e.g. Tower Crane TC-1'}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {form.resource_type === 'labor' && (
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Workers *</label>
                    <input type="number" value={form.workers_count} onChange={e=>h('workers_count', e.target.value)}
                      placeholder="5"
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                  </div>
                )}
                <div className={form.resource_type === 'equipment' ? 'col-span-2' : ''}>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Qty Executed *</label>
                  <input type="number" value={form.qty_executed} onChange={e=>h('qty_executed', e.target.value)}
                    placeholder="8.5"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Sub Code (Cost Control coding — leave empty if Site is entering this)</label>
                <input list="sad-sc-list" value={form.sub_code} onChange={e=>h('sub_code', e.target.value)}
                  placeholder="e.g. S-C.Dew.1"
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm font-mono"/>
                <datalist id="sad-sc-list">
                  {subCodes.map(s => <option key={s.sub_code} value={s.sub_code}>{s.sub_code} — {s.description}</option>)}
                </datalist>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Notes</label>
                <input value={form.notes} onChange={e=>h('notes', e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm">Cancel</button>
              <button onClick={handleSave} className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">
                {editEntry ? 'Save Changes' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
